import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { ArrowTopRightOnSquareIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';

import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import { cn } from '@dashboard/lib/utils';

const propTypes = {
  to: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
    PropTypes.element,
  ]),
  onClick: PropTypes.func,
  handleClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  appearance: PropTypes.oneOf([
    'primary',
    'icon-button',
    'bare',
    'secondary',
    'light',
    'link',
    'subtle',
    'ai',
  ]),
  className: PropTypes.string,
};

const defaultProps = {
  to: '',
  handleClick: null,
  disabled: false,
  appearance: 'secondary',
};

// Legacy appearance → shadcn variant
const appearanceToVariant = appearance => {
  switch (appearance) {
    case 'primary':
      return 'default';
    case 'secondary':
    case 'light':
      return 'outline';
    case 'subtle':
      return 'secondary';
    case 'link':
      return 'link';
    case 'bare':
    case 'icon-button':
      return 'ghost';
    case 'ai':
      return 'outline';
    default:
      return 'outline';
  }
};

// Legacy size → shadcn size
const sizeToShadcn = (size, appearance) => {
  if (appearance === 'icon-button') return 'icon';
  switch (size) {
    case 'small':
      return 'sm';
    case 'xs':
      return 'xs';
    case 'narrow':
      return 'sm';
    case 'full':
      return 'lg';
    case 'base':
    default:
      return 'default';
  }
};

// Style overrides where the legacy API exposes things shadcn doesn't have built-in
const legacyOverrides = ({ appearance, size }) =>
  cn({
    'w-full justify-center': size === 'full',
    '!px-0': size === 'narrow',
    'underline !p-0': appearance === 'link',
    // ai: animated gradient border. `!` important on background-clip
    // overrides the base button's `bg-clip-padding` so the gradient border
    // layer is visible (clipped to border-box). 1px border keeps height
    // identical to other button variants.
    'relative overflow-hidden !border-transparent ![background-image:linear-gradient(white,white),linear-gradient(to_right,rgb(167,139,250),rgb(232,121,249),rgb(244,114,182))] ![background-origin:border-box] ![background-clip:padding-box,border-box] hover:![background-image:linear-gradient(white,white),linear-gradient(to_right,rgb(139,92,246),rgb(217,70,239),rgb(236,72,153))] dark:![background-image:linear-gradient(var(--background),var(--background)),linear-gradient(to_right,rgb(167,139,250),rgb(232,121,249),rgb(244,114,182))] dark:hover:![background-image:linear-gradient(var(--accent),var(--accent)),linear-gradient(to_right,rgb(139,92,246),rgb(217,70,239),rgb(236,72,153))]':
      appearance === 'ai',
  });

// Backwards-compat: returns the shadcn button class string. Used by call sites
// that style non-button elements (e.g., wrappers, anchors) like a button.
export function getButtonClass(appearance, size) {
  const variant = appearanceToVariant(appearance);
  const shadSize = sizeToShadcn(size, appearance);
  return cn(buttonVariants({ variant, size: shadSize }), legacyOverrides({ appearance, size }));
}

// Backwards-compat: a few call sites import StyledButton directly. Render shadcn.
export const StyledButton = forwardRef(function StyledButton({ className, ...props }, ref) {
  return <ShadcnButton ref={ref} className={className} {...props} />;
});

const Button = forwardRef(function Button(
  {
    to,
    href,
    className,
    target,
    size = 'base',
    appearance = 'secondary',
    type = 'button',
    external = false,
    handleClick = null,
    children,
    onClick,
    rel,
    loading = false,
    // `wide` was a legacy padding bump; absorbed here so it doesn't leak to DOM.
    wide: _wide,
    ...rest
  },
  ref,
) {
  const handler = onClick || handleClick || undefined;
  const variant = appearanceToVariant(appearance);
  const shadSize = sizeToShadcn(size, appearance);
  const overrides = legacyOverrides({ appearance, size });

  const linkProps = href || to ? { href, to, target, rel } : {};

  return (
    <ShadcnButton
      ref={ref}
      type={href || to ? undefined : type}
      variant={variant}
      size={shadSize}
      className={cn(overrides, className)}
      onClick={handler}
      {...linkProps}
      {...rest}
    >
      {loading ? (
        <Loader2
          aria-hidden="true"
          className={cn(
            'size-4 animate-spin',
            appearance === 'ai' && 'relative z-10 text-violet-500',
          )}
        />
      ) : (
        appearance === 'ai' && <SparklesIcon className="size-4 relative z-10 text-violet-500" />
      )}
      {appearance === 'ai' ? <span className="relative z-10">{children}</span> : children}
      {external && <ArrowTopRightOnSquareIcon className="size-4" />}
    </ShadcnButton>
  );
});

Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

export default Button;
